/**
 * One cell of the routine grid (D11).
 *
 * The rule this component exists to keep: **a snapshot never overwrites the
 * cell you are typing in.** The grid is live — the owner's phone, another tab,
 * or this write's own echo can deliver a new value mid-keystroke — and a
 * plainly controlled input would drop the character under the cursor.
 *
 * So the cell is locally controlled from focus until commit, and takes the
 * incoming value only while it is idle. On commit the local value wins,
 * because it is the more recent human intent.
 *
 * Keys (D10): `Enter` commits and hands focus down the same column, `Escape`
 * reverts to the stored value, blur commits. Committing an unchanged value
 * writes nothing.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { cn } from '../../lib/utils';

interface Props {
  value: string;
  onCommit: (next: string) => void;
  /** Move focus down the same column — what `Enter` is for in a grid. */
  onEnter?: () => void;
  placeholder?: string;
  ariaLabel: string;
  /** Select the text once, for a section that was just created. */
  autoFocus?: boolean;
  className?: string;
  /** Numeric cells get a narrow, right-aligned box and a number keypad. */
  numeric?: boolean;
  /** Kanban titles wrap; an `<input>` cannot. Caps at this many lines. */
  rows?: number;
}

export default function EditableCell({
  value,
  onCommit,
  onEnter,
  placeholder,
  ariaLabel,
  autoFocus = false,
  className = '',
  numeric = false,
  rows = 1,
}: Props) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const didAutoFocus = useRef(false);

  // Idle cells follow the document; a focused one does not (D11).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!autoFocus || didAutoFocus.current) return;
    didAutoFocus.current = true;
    const el = rows > 1 ? areaRef.current : inputRef.current;
    el?.focus();
    el?.select();
    setEditing(true);
  }, [autoFocus, rows]);

  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el || rows <= 1) return;
    el.style.height = 'auto';
    const line = Number.parseFloat(getComputedStyle(el).lineHeight) || 16;
    el.style.height = `${Math.min(el.scrollHeight, line * rows)}px`;
  }, [draft, rows]);

  const commit = () => {
    setEditing(false);
    const next = (rows > 1 ? draft.replace(/\n+/g, ' ') : draft).trim();
    if (next === value.trim()) return;
    onCommit(next);
  };

  const fieldClass = cn(
    'w-full rounded-md border border-transparent bg-transparent px-1 py-0.5 text-sm text-foreground outline-none transition hover:border-border focus:border-ring focus:bg-background',
    numeric && 'text-right tabular-nums',
    className,
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit();
      onEnter?.();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(value);
      setEditing(false);
      (rows > 1 ? areaRef.current : inputRef.current)?.blur();
    }
  };

  if (rows > 1) {
    return (
      <textarea
        ref={areaRef}
        value={draft}
        aria-label={ariaLabel}
        placeholder={placeholder}
        rows={1}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => setEditing(true)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        className={`resize-none overflow-hidden ${fieldClass}`}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      aria-label={ariaLabel}
      placeholder={placeholder}
      inputMode={numeric ? 'numeric' : undefined}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setEditing(true)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      className={fieldClass}
    />
  );
}
